import { useMemo, useState, useEffect } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import {
  Text,
  Alert,
  FormLabel,
  Modal,
  Button,
  Badge,
  Icon,
  BadgeProps,
  useToast,
  Toast,
} from "@oysterhr/ui"
import * as Yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { NavigationButtons } from "~/shared/components/NavigationButtons"
import { useReusableWizard, NumericTextInput } from "@oysterhr/feature-components"
import { companyPaymentMethodsPath } from "@oysterhr/router"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { PaymentMethodStripeAch } from "@oysterhr/schema"
import { GET_STRIPE_ACH_PAYMENT_METHOD } from "../../hooks/useStripeAchPaymentMethod"
import { useDeletePaymentMethod } from "../../../../../actions"
import { useVerifyStripeAchPaymentMethod } from "../../hooks/useVerifyStripeAchPaymentMethod"

export interface VerificationStepProps {
  paymentMethod: PaymentMethodStripeAch
}

type AmountValues = {
  amount1: string
  amount2: string
}

type Attempt = 0 | 1 | 2 | 3

const VERIFICATION_ATTEMPTS_TO_MODAL_CONTENT: Record<Attempt, string> = {
  0: "verification.modal.content.noRemaining",
  1: "verification.modal.content.oneRemaining",
  2: "verification.modal.content.otherRemaining",
  3: "verification.modal.content.otherRemaining",
}

const VERIFICATION_ATTEMPTS_TO_BADGE_VARITATION: Record<Attempt, BadgeProps["variation"]> = {
  0: "error",
  1: "warning",
  2: "gray",
  3: "gray",
}

const MAX_VERIFICATION_ATTEMPTS_COUNT = 3

export const VerificationStep = ({ paymentMethod }: VerificationStepProps) => {
  const toast = useToast()
  const navigate = useNavigate()
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/AchBankDetailsFlow")
  const [verifyStripeAchMethod, { loading: isVerifying }] = useVerifyStripeAchPaymentMethod()
  const { goToNext } = useReusableWizard()
  const { accountAspect: companyId } = useAccountAspect()
  const { deletePaymentMethod, loading: isDeleting } = useDeletePaymentMethod({
    successCallback: () => {
      navigate(companyPaymentMethodsPath(companyId))
    },
    failureCallback: errors => {
      toast({
        render: (
          <Toast variation="error">
            <Toast.Content>{errors.join(", ")}</Toast.Content>
          </Toast>
        ),
      })
    },
  })
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false)

  const remainingVerificationAttempts = Math.max(
    MAX_VERIFICATION_ATTEMPTS_COUNT - (paymentMethod.verificationAttemptsCount || 0),
    0,
  ) as Attempt

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        amount1: Yup.number()
          .typeError(t("verification.validations.type"))
          .moreThan(0, t("verification.validations.moreThan0"))
          .required(t("verification.validations.required")),
        amount2: Yup.number()
          .typeError(t("verification.validations.type"))
          .moreThan(0, t("verification.validations.moreThan0"))
          .required(t("verification.validations.required")),
      }),
    [t],
  )

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: { amount1: "", amount2: "" },
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = async (values: AmountValues) => {
    const { data } = await verifyStripeAchMethod({
      variables: {
        paymentMethodId: paymentMethod.id,
        amount1: Number(values.amount1),
        amount2: Number(values.amount2),
      },
      awaitRefetchQueries: true,
      refetchQueries: [GET_STRIPE_ACH_PAYMENT_METHOD],
    })

    if (data?.verifyStripeAchPaymentMethod?.errors.length === 0) {
      goToNext()
    } else {
      setShowErrorModal(true)
    }
  }

  useEffect(() => {
    if (remainingVerificationAttempts === 0) setShowErrorModal(true)
  }, [remainingVerificationAttempts])

  const handleDelete = async () => {
    await deletePaymentMethod(paymentMethod)
  }

  const showRemoveButton = remainingVerificationAttempts === 0

  return (
    <>
      <Text className="mb-4 mt-10">{t("verification.title")}</Text>
      <Alert variation="warning">
        <Alert.Icon />
        <Alert.Heading>{t("verification.alert.title")}</Alert.Heading>
        <Alert.Content>{t("verification.alert.description")}</Alert.Content>
      </Alert>

      <Badge
        isOutline
        variation={VERIFICATION_ATTEMPTS_TO_BADGE_VARITATION[remainingVerificationAttempts]}
        className="mt-4"
      >
        {remainingVerificationAttempts === 1 ? <Icon name="WarningTriangle" /> : null}
        {t("verification.attempt", {
          count: remainingVerificationAttempts,
        })}
      </Badge>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mt-4">
          <NumericTextInput
            {...register("amount1")}
            label={
              <FormLabel
                label={t("verification.labels.amount1")}
                assistiveText={t("verification.assistiveText")}
              />
            }
            type="number"
            step={1}
            min={0}
            max={99}
            placeholder="00"
            error={errors?.amount1?.message}
          />
        </div>
        <div className="mt-4">
          <NumericTextInput
            {...register("amount2")}
            label={
              <FormLabel
                label={t("verification.labels.amount2")}
                assistiveText={t("verification.assistiveText")}
              />
            }
            type="number"
            step={1}
            min={0}
            max={99}
            placeholder="00"
            error={errors?.amount2?.message}
          />
        </div>
        <NavigationButtons isFirst loading={isVerifying} analyticsBase="verify-ach-bank-details" />
        <Modal isOpen={showErrorModal} onRequestClose={() => setShowErrorModal(false)}>
          <Modal.Header>
            {remainingVerificationAttempts === 0
              ? t("verification.modal.title.noRemaining")
              : t("verification.modal.title.default")}
          </Modal.Header>
          <Modal.Content>
            <Text>
              <Trans
                i18nKey={VERIFICATION_ATTEMPTS_TO_MODAL_CONTENT[remainingVerificationAttempts]}
                t={t}
                components={{
                  strong: <strong className="font-semibold" />,
                }}
              />
            </Text>
          </Modal.Content>
          <Modal.Footer>
            <Button variation="outline" onClick={() => setShowErrorModal(false)}>
              {showRemoveButton ? t("verification.cancelButton") : t("verification.closeButton")}
            </Button>
            {showRemoveButton && (
              <Button variation="danger" onClick={handleDelete} isLoading={isDeleting}>
                {t("verification.removeButton")}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </form>
    </>
  )
}
